import { computePosition, flip, shift, offset, autoUpdate }
  from 'https://cdn.jsdelivr.net/npm/@floating-ui/dom@1.7.5/+esm';

// ── custom-popover (state owner) ────────────────────────────────────────────
customElements.define('custom-popover', class CustomPopover extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('custom-popover-toggle');
    this.body   = this.querySelector('custom-popover-body');
    this._cleanup = null;
    this._placeholder = null;
    this._rafId = null;
    this._overlay = null;
    this._cardClone = null;
    this._cardCloneObserver = null;

    this._onToggle       = () => this.isOpen ? this.close() : this.open();
    this._onKeydown      = (e) => e.key === 'Escape' && this.isOpen && this.close(true);
    this._onOutsideClick = (e) =>
      this.isOpen && !this.contains(e.target) && !this.body?.contains(e.target) && this.close();
    this._onButtonClick  = (e) => { if (e.target.closest('button')) e.preventDefault(); };

    this._accordionCleanups = [...this.querySelectorAll('.card-option-accordion')]
      .map(initCardAccordion)
      .filter(Boolean);

    this.addEventListener('custom-popover:toggle', this._onToggle);
    this.addEventListener('click', this._onButtonClick);
    document.addEventListener('keydown', this._onKeydown);
    document.addEventListener('click', this._onOutsideClick, true);
  }

  disconnectedCallback() {
    this.removeEventListener('custom-popover:toggle', this._onToggle);
    this.removeEventListener('click', this._onButtonClick);
    document.removeEventListener('keydown', this._onKeydown);
    document.removeEventListener('click', this._onOutsideClick, true);
    if (this._rafId) { cancelAnimationFrame(this._rafId); this._rafId = null; }
    this._cleanup?.();
    this._accordionCleanups?.forEach(fn => fn());
    this._accordionCleanups = null;
    this._removeOverlay();
    this._returnCardFromPortal();
    this._returnBodyToSlot();
    this.body?.remove();
  }

  get isOpen() { return this.hasAttribute('open'); }

  _addOverlay() {
    if (this._overlay) return;
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'page-overlay page-overlay-popover';
    btn.setAttribute('aria-hidden', 'true');
    btn.setAttribute('tabindex', '-1');
    btn.addEventListener('click', () => this.close());
    document.body.appendChild(btn);
    this._overlay = btn;
  }

  _portalCard() {
    if (this._cardClone) return;
    const rect = this.getBoundingClientRect();
    const clone = this.cloneNode(true);
    clone.removeAttribute('data-teleport');
    clone.setAttribute('teleported', null)
    Object.assign(clone.style, {
      position: 'absolute',
      top: `${rect.top + window.scrollY}px`,
      left: `${rect.left + window.scrollX}px`,
      width: `${rect.width}px`,
      zIndex: '1000',
      margin: '0',
    });
    this.style.visibility = 'hidden';
    document.body.appendChild(clone);
    this._cardClone = clone;

    const cloneBody = clone.querySelector('custom-popover-body');
    const observer = new MutationObserver(() => {
      if (!clone.hasAttribute('open')) {
        observer.disconnect();
        // Swap visibility immediately so the clone card never shows without its
        // open popover body (which is still fading out on the now-hidden clone).
        clone.style.visibility = 'hidden';
        this.style.visibility = '';
        let done = false;
        const finish = () => { if (!done) { done = true; this._returnCardFromPortal(); } };
        cloneBody?.addEventListener('transitionend', finish, { once: true });
        setTimeout(finish, 300);
      }
    });
    observer.observe(clone, { attributes: true, attributeFilter: ['open'] });
    this._cardCloneObserver = observer;

    clone.open();
  }

  _returnCardFromPortal() {
    if (!this._cardClone) return;
    this._cardCloneObserver?.disconnect();
    this._cardCloneObserver = null;
    this._cardClone.remove();
    this._cardClone = null;
    this.style.visibility = '';
  }

  _removeOverlay() {
    this._overlay?.remove();
    this._overlay = null;
  }

  _portalBody() {
    if (this._placeholder || !this.body) return;
    this._placeholder = document.createComment('custom-popover-body');
    this.body.parentNode.insertBefore(this._placeholder, this.body);
    document.body.appendChild(this.body);
  }

  _returnBodyToSlot() {
    if (!this._placeholder) return;
    this._placeholder.parentNode?.insertBefore(this.body, this._placeholder);
    this._placeholder.remove();
    this._placeholder = null;
  }

  open() {
    if (this.isOpen) return;

    if (this.closest('gridy-slider')) {
      this._portalCard();
      return;
    }

    this.setAttribute('open', '');
    this.toggle?.setExpanded(true);
    if (!this.body) return;

    if (this.hasAttribute('data-teleport')) this._portalBody();
    this._addOverlay();
    this.body.style.visibility = 'visible';


    const anchor = this.dataset.anchor === 'self' ? this : this.toggle;

    this._cleanup = autoUpdate(anchor, this.body, () => {
      computePosition(anchor, this.body, {
        strategy: 'absolute',
        placement: 'bottom-end',
        middleware: [shift({ padding: 8 })],
      }).then(({ x, y }) => {
        const styles = { left: `${x}px`, top: `${y}px` };
        if (this.dataset.anchor === 'self') {
          styles.width = `${anchor.getBoundingClientRect().width}px`;
        }
        Object.assign(this.body.style, styles);
      });
    });

    this._rafId = requestAnimationFrame(() => {
      this._rafId = null;
      this.body.classList.add('is-open');
    });
  }

  close(returnFocus = false) {
    if (!this.isOpen) return;
    this.removeAttribute('open');
    this.toggle?.setExpanded(false);

    if (this._rafId) {
      // open()'s rAF hasn't fired yet — is-open was never applied, so no
      // transition will occur and transitionend will never fire.
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
      if (this.body) {
        this.body.style.visibility = 'hidden';
        this._returnBodyToSlot();
      }
    } else {
      this.body?.classList.remove('is-open');
      const hide = () => {
        if (!this.isOpen) {
          this.body.style.visibility = 'hidden';
          this._returnBodyToSlot();
        }
      };
      this.body?.addEventListener('transitionend', hide, { once: true });
    }

    this._cleanup?.();
    this._cleanup = null;
    this._removeOverlay();

    if (returnFocus) this.toggle?.querySelector('button')?.focus();
  }
});

// ── custom-popover-toggle ────────────────────────────────────────────────────
customElements.define('custom-popover-toggle', class CustomPopoverToggle extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    if (!this.button) return;

    const bodyId = `popover-body-${Math.random().toString(36).slice(2, 7)}`;
    const body = this.closest('custom-popover')?.querySelector('custom-popover-body');
    if (body) body.id = bodyId;

    this.button.setAttribute('aria-expanded', 'false');
    this.button.setAttribute('aria-haspopup', 'true');
    this.button.setAttribute('aria-controls', bodyId);

    this.button.addEventListener('click', () =>
      this.dispatchEvent(new CustomEvent('custom-popover:toggle', { bubbles: true }))
    );
  }

  setExpanded(value) {
    this.button?.setAttribute('aria-expanded', String(value));
  }
});

// ── custom-popover-body ──────────────────────────────────────────────────────
customElements.define('custom-popover-body', class CustomPopoverBody extends HTMLElement {
  connectedCallback() {
    Object.assign(this.style, { position: 'absolute', top: '0', left: '0' });
    this.setAttribute('role', 'region');
  }
});

// ── card-option-accordion: animated open/close + exclusive group ──────────────
function initCardAccordion(details) {
  if (details._cardAccordionInit) return;
  details._cardAccordionInit = true;

  const summary = details.querySelector(':scope > summary');
  if (!summary) return;

  let anim      = null;
  let isClosing = false;
  let isOpening = false;

  function openAccordion() {
    const startH = details.offsetHeight;
    details.open = true;
    const endH = details.offsetHeight;
    details.style.overflow = 'hidden';
    details.style.height   = startH + 'px';

    if (anim) anim.cancel();
    isOpening = true;
    anim = details.animate(
      { height: [startH + 'px', endH + 'px'] },
      { duration: 250, easing: 'ease-out' }
    );
    anim.onfinish = () => {
      details.style.height = details.style.overflow = '';
      anim = null; isOpening = false;
    };
    anim.oncancel = () => { isOpening = false; };
  }

  function closeAccordion() {
    if (isClosing || (!details.open && !isOpening)) return;
    const startH = details.offsetHeight;
    details.style.overflow = 'hidden';

    if (anim) anim.cancel();
    isClosing = true;
    anim = details.animate(
      { height: [startH + 'px', summary.offsetHeight + 'px'] },
      { duration: 250, easing: 'ease-in' }
    );
    anim.onfinish = () => {
      details.open = false;
      details.style.height = details.style.overflow = '';
      anim = null; isClosing = false;
    };
    anim.oncancel = () => { isClosing = false; };
  }

  const onClick = (e) => {
    e.preventDefault();
    if (isClosing || !details.open) openAccordion(); else closeAccordion();
  };
  summary.addEventListener('click', onClick);

  return () => {
    summary.removeEventListener('click', onClick);
    if (anim) { anim.cancel(); anim = null; }
    details.style.height = details.style.overflow = '';
    details._cardAccordionInit = false;
  };
}

document.addEventListener('ajaxProduct:added', () => {
  document.querySelectorAll('custom-popover[open]').forEach(el => el.close());
});
